import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import { redeemInvitation, type Session } from '@/api/auth';
import type { ApiError } from '@/api/http';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { readerChanged } from '@/app/queryClient';
import { cn } from '@/lib/cn';
import { TenantMark } from '@/theme/TenantMark';
import { type } from '@/theme/typography';

/**
 * ⚠️ **Only that something was typed.** The rules are Django's `AUTH_PASSWORD_VALIDATORS` — length,
 * commonness, similarity to the account — and they live on the server, which is the only place that
 * knows them and the only place that can be trusted to apply them. A client-side minimum invented here
 * would be a second, different rule: a value this screen accepted and the server refused, or worse, one
 * this screen refused that the product would have allowed.
 */
const redemptionSchema = z.object({ password: z.string().min(1, 'passwordRequired') });

type RedemptionForm = z.infer<typeof redemptionSchema>;

/**
 * Which failure this is — because the sentence, the control **and** whose words are used differ by
 * cause, and getting that wrong on this screen costs the reader their invitation (BUG-049).
 *
 * ⚠️ **The server's own sentence is rendered on 422 and nowhere else, and that is the rule this
 * function exists to state.** A 422 here is the API's single deliberate refusal for the **token** —
 * unknown, expired, already used, replaced, all one answer so that a public route cannot be asked
 * whether an account exists. ⚠️ **It is not the whole set**: a password failing Django's validators,
 * or over the server's length bound (BUG-058), is the same 422 carrying its own sentence — which is
 * why this branch renders `detail` rather than a fixed string. Its wording is written for a reader,
 * which is why §6.10 leans on it rather than
 * paraphrasing. The other two refusals this route can give are **framework** wording: django-ninja
 * answers a CSRF failure `"CSRF check Failed"` and a throttle `"Request was throttled."`, and until
 * this existed the screen rendered `error.detail` for every non-offline failure, so an invitee with
 * no password, no session and nothing to act on was shown the first of those verbatim (measured
 * against the deployed dev API). §8.3 asks for *"what failed, in plain language, and a Retry"*.
 *
 * ⚠️ **`stale` must not say the link is dead, and neither must anything else here.** A reader told
 * that asks an Admin for a replacement, and a reissue invalidates the link still in their hand —
 * BUG-031's lesson, learnt on the offline branch and applying identically to both refusals a person
 * meets through no fault of their own.
 *
 * `throttled` reuses **sign-in's** sentence rather than a second one for the same fact: the
 * invitation route is throttled at 5/min per IP and *"Too many attempts. Wait a minute and try
 * again."* is already the product's way of saying so.
 */
type InviteFailure = 'offline' | 'stale' | 'throttled' | 'dead';

function failureOf(error: ApiError): InviteFailure {
  if (error.isOffline) return 'offline';
  if (error.status === 403) return 'stale';
  if (error.isThrottled) return 'throttled';
  return 'dead';
}

/**
 * Redeeming a one-time invitation — the screen behind the link an Admin hands over (TASK-101).
 *
 * ⚠️ **It is outside the shell and outside the session**, like `/sign-in`: the person arriving has no
 * account they can sign in to yet, which is what the token is for. It wears the tenant's brand
 * (`TenantMark`, and `TenantBrand` sets §2's properties above the router) because this may be the first
 * page they ever see of the product, and a page that does not look like the workspace they were told
 * about is a page people are right to distrust.
 *
 * ⚠️ **No email field, and none is offered.** Everything the server needs is derived from the token, so
 * the person does not have to know which address their account was created with — and the URL carries
 * no email either, because a URL reaches an access log.
 *
 * ⚠️ **The token comes out of the URL *fragment*, not a route parameter** (BUG-028; see
 * `inviteUrls.ts` for the whole argument). A fragment is the one part of a URL a browser never sends,
 * so the credential reaches this screen without reaching any access log. It is stripped of its
 * leading `#` and of **nothing else**: no `decodeURIComponent`, because the token is
 * `secrets.token_urlsafe(32)` — `A-Za-z0-9-_`, which a browser never percent-encodes — so decoding
 * would buy nothing today and, the day that alphabet admitted a `%`, would *consume* it: a token
 * containing `%2B` would be handed to the server as `+`, spending an invitation on a value it never
 * issued and telling the reader their link was dead. (⚠️ It would **not** turn a `+` into a space —
 * that is `application/x-www-form-urlencoded` parsing, `URLSearchParams` and `unescape`, not
 * `decodeURIComponent`, which leaves `+` alone. This docblock said otherwise until BUG-028's review
 * measured it, and the sentence is load-bearing: it is what a future reader will use to decide
 * whether adding decoding is safe.)
 *
 * ⚠️ **A dropped connection is not a dead link, and saying so cost a working invitation** (BUG-031).
 * The offline branch below exists because an offline failure is `ApiError(0, …)` with no `detail`: it
 * fell through to *"This link no longer works."*, which sends the reader to ask an Admin for a
 * replacement — and a reissue invalidates the link they are still holding.
 *
 * ⚠️ **Success signs them in and lands them on the dashboard.** That is the server's decision (it
 * answers `MeResponse`, the same payload login returns) and this screen honours it rather than sending
 * them to a sign-in form to type what they typed a second ago.
 *
 * ⚠️ **A dead link gets the server's one sentence, and this screen adds nothing to it.** Unknown,
 * expired, already used and replaced are one answer on purpose — a public route that distinguished them
 * would be an oracle for whether an account exists — so guessing here would either be wrong or would
 * leak precisely what the server refuses to say. What the screen does add is the way out: the sign-in
 * page, for somebody whose link was dead because they had already used it.
 *
 * ⚠️ **That is the 422, and only the 422** (BUG-049). This screen rendered `error.detail` for every
 * non-offline failure, and the other two refusals the route can give carry django-ninja's words
 * rather than the product's: a CSRF failure reached the reader as *"CSRF check Failed"* and a
 * throttle as *"Request was throttled."* Which sentence belongs to which cause is decided in
 * `failureOf` below, with the reasoning.
 */
export default function Invite() {
  const { t } = useTranslation(['auth', 'common']);
  // `useLocation` rather than `window.location`, because this is a router app: a hash the router
  // navigated to is in its own state, and reading the global would miss it. `slice(1)` drops the
  // leading `#` and touches nothing else — see the docblock above.
  const token = useLocation().hash.slice(1);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<RedemptionForm>({
    resolver: zodResolver(redemptionSchema),
    defaultValues: { password: '' },
  });

  const redeem = useMutation<Session, ApiError, RedemptionForm>({
    mutationFn: ({ password }) => redeemInvitation({ token, password }),
    onSuccess: (session) => {
      /**
       * ⚠️ **Redemption is a sign-in, so the brand changes here too** (BUG-045).
       * `GET /api/v1/branding/` answers the session's tenant now and the host's only for a caller
       * without one, and this screen has been rendering the *host's* brand since it loaded —
       * anonymously, correctly. The invited person's tenant need not be the host's: login is global
       * (ADR 0013) and the constraint that would have made them agree was the option the user
       * declined. Without this they land on the dashboard wearing whoever the host belongs to.
       *
       * Everything else goes with it for the same reason sign-in empties the cache: a tab that held
       * a session before this one must not hand its screens to the person redeeming the link — and
       * `readerChanged` is the one list of what that means, so this door cannot fall behind the
       * others the way sign-out's chart span did (BUG-066).
       */
      readerChanged(queryClient, { emptyTheCache: true });
      // The session is live the moment the server answers; seeding the cache means the dashboard
      // renders without a second `/auth/me/` round trip, exactly as sign-in does.
      queryClient.setQueryData(queryKeys.session(), session);
      void navigate('/', { replace: true });
    },
  });

  const failure = redeem.error === null ? null : failureOf(redeem.error);
  /**
   * The sentence for that cause. The dead-link branch is the only one that renders the API's own
   * `detail`, and only from a 422 — a 500 (whose body in production is HTML and carries no `detail`
   * at all) falls back to the screen's sentence exactly as it did before.
   */
  const message =
    failure === 'offline'
      ? t('invite.offline')
      : failure === 'stale'
        ? t('invite.stale')
        : failure === 'throttled'
          ? t('throttled')
          : redeem.error?.status === 422 && redeem.error.detail
            ? redeem.error.detail
            : t('invite.failed');

  return (
    <main className="flex min-h-screen items-center justify-center bg-paper-bg px-8">
      <div className="w-full max-w-sm">
        <TenantMark fallback={t('product')} />
        <h1 className={`${type.pageTitle} mt-2`}>{t('invite.title')}</h1>
        <p className={cn(type.body, 'mt-2')}>{t('invite.lead')}</p>

        {/*
          ⚠️ **An address with no fragment is refused here rather than sent** (BUG-028's review). The
          secret now lives in the one part of a URL intermediaries are most likely to drop: a chat
          client that linkifies only up to the `#`, a mail gateway that rewrites links, a reader who
          double-click-selects the address and misses the tail. Their invitation is still perfectly
          live — but posting `token: ""` earns a `422`, whose sentence is *"This link no longer
          works"*, which is written for a different cause and sends them to ask for a replacement
          that would invalidate the link they are holding. It also spends one of five
          `INVITATION_THROTTLE_RATE` attempts per IP per minute, so retrying answers 429.

          So this is the one case the screen can diagnose better than the server can: the server is
          handed nothing and cannot tell a truncated link from a wrong one. Rendered instead of the
          form, because there is nothing to fill in.
        */}
        {token === '' ? (
          <div className="mt-4 rounded border border-negative bg-negative-wash p-3">
            <p className="text-sm text-negative">{t('invite.incomplete')}</p>
          </div>
        ) : (
          <>
            {failure !== null && (
              <div className="mt-4 rounded border border-negative bg-negative-wash p-3">
                {/*
                  §8's failure states for this screen, told apart by cause in `failureOf` — the
                  offline one says what is unknown (whether the password was set), the stale one says
                  the link is still good, and both name the action that must **not** be taken:
                  asking for a replacement link invalidates the one in the reader's hand.
                */}
                <p className="text-sm text-negative">{message}</p>

                {/*
                  No `isLoading` on the retry below, and deliberately: the notice is rendered on
                  `isError`, which a retry clears on its first tick, so this button cannot be on
                  screen while its own request is in flight — the *submit* button below is what
                  spins (§4 Forms). A busy prop that can never be true is a claim nothing can check.
                */}
                {failure === 'offline' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-2"
                    onClick={() => redeem.mutate(form.getValues())}
                  >
                    {t('common:retry')}
                  </Button>
                )}

                {/*
                  ⚠️ **A retry that reloads, and that is the substance of the fix** (BUG-049).
                  Posting again re-uses the same missing or rejected cookie and fails identically,
                  so a plain *Try again* here would be a control that cannot work — §8's "a state is
                  not a dead end" met in appearance only. Reloading re-runs the whole screen, and
                  with it `GET /auth/csrf/` from a fresh page. The token survives it: it lives in the
                  fragment (BUG-028), which `location.reload()` keeps. The typed password does not,
                  which is why the sentence says to set it again rather than promising the form will
                  be as they left it.
                */}
                {failure === 'stale' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-2"
                    onClick={() => window.location.reload()}
                  >
                    {t('invite.reload')}
                  </Button>
                )}

                {/*
                  ⚠️ The way out belongs to **this** branch only. It is the one action a person who
                  has not set a password cannot use, and the likeliest reason for a refusal here is a
                  link already spent — by them, on a first visit they have forgotten. A throttle and
                  a stale page are both retryable, so neither offers it.
                */}
                {failure === 'dead' && (
                  <a href="/sign-in" className="mt-2 inline-block text-xs font-medium underline">
                    {t('invite.toSignIn')}
                  </a>
                )}
              </div>
            )}

            <form
              className="mt-6 space-y-4"
              onSubmit={form.handleSubmit((values) => redeem.mutate(values))}
              noValidate
            >
              <Field
                label={t('invite.password')}
                required
                description={t('invite.passwordHint')}
                error={form.formState.errors.password ? t('invite.passwordRequired') : undefined}
              >
                {(fieldProps) => (
                  <Input
                    {...fieldProps}
                    {...form.register('password')}
                    type="password"
                    autoComplete="new-password"
                    autoFocus
                  />
                )}
              </Field>

              <Button type="submit" variant="primary" isLoading={redeem.isPending}>
                {t('invite.submit')}
              </Button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
